import React from 'react';
import BaseDialog from './BaseDialog';
import Button from './Button';
import { FormField, Input, Select } from './FormField';
import MultiSelect from './MultiSelect';

/**
 * OfficerDialog Component
 * 
 * Unified dialog for adding or editing officers
 * Follows DRY principle by combining Add and Edit functionality
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {string} mode - 'add' or 'edit'
 * @param {object} officer - Officer data object
 * @param {function} onOfficerChange - Callback when officer data changes
 * @param {function} onSubmit - Callback when user submits the form
 * @param {function} onCancel - Callback when user cancels
 * @param {array} designations - List of available designations
 * @param {array} departments - List of available departments
 * @param {array} authorityPrivileges - List of authority privileges for menu_ids
 * @param {boolean} loading - Loading state for async operations
 */
const OfficerDialog = ({
  isOpen,
  mode = 'add',
  officer = {},
  onOfficerChange,
  onSubmit,
  onCancel,
  designations = [],
  departments = [],
  authorityPrivileges = [],
  loading = false,
  hideDepartmentField = false,
  defaultDepartment = null,
}) => {
  // Auto-fill department when dialog opens - compare user's department ID with dropdown options
  // This hook must be called before any early returns
  React.useEffect(() => {
    if (isOpen && hideDepartmentField && defaultDepartment && departments.length > 0) {
      // Find the matching department in the dropdown list
      const matchingDepartment = departments.find(dept => {
        const deptId = typeof dept === 'object' ? dept.id : dept;
        return String(deptId) === String(defaultDepartment);
      });

      if (matchingDepartment) {
        const deptId = typeof matchingDepartment === 'object' ? matchingDepartment.id : matchingDepartment;
        // Always set department to current user's department when hideDepartmentField is true
        if (mode === 'add' && !officer.department) {
          onOfficerChange({ ...officer, department: String(deptId) });
        } else if (mode === 'edit') {
          // For edit mode, ensure department matches current user's department
          const currentDeptId = typeof officer.department === 'object' 
            ? officer.department.id 
            : officer.department;
          if (String(currentDeptId) !== String(deptId)) {
            onOfficerChange({ ...officer, department: String(deptId) });
          }
        }
      }
    }
  }, [isOpen, mode, hideDepartmentField, defaultDepartment, departments, officer, onOfficerChange]);

  if (!isOpen) return null;

  const title = mode === 'add' ? 'Add New Officer' : 'Edit Officer';
  const submitButtonText = mode === 'add' ? 'Save Officer' : 'Update Officer';

  const handleFieldChange = (field, value) => {
    const updatedOfficer = { ...officer, [field]: value };
    // If department field is hidden, always set it to defaultDepartment
    if (hideDepartmentField && field !== 'department' && defaultDepartment) {
      updatedOfficer.department = defaultDepartment;
    }
    onOfficerChange(updatedOfficer);
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onSubmit} disabled={loading}>
        {loading ? 'Saving...' : submitButtonText}
      </Button>
    </>
  );

  return (
    <BaseDialog
      isOpen={isOpen}
      title={title}
      onClose={onCancel}
      footer={footer}
      size="medium"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label="Employment ID" required>
          <Input
            id="officer-empid"
            value={officer.employmentId}
            onChange={(e) => handleFieldChange('employmentId', e.target.value)}
            placeholder="Enter employment id"
            disabled={loading}
          />
        </FormField>

        <FormField label="Name" required>
          <Input
            id="officer-name"
            value={officer.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder="Enter officer name"
            disabled={loading}
          />
        </FormField>

        <FormField label="Designation" required>
          <Select
            id="officer-designation"
            value={officer.designation}
            onChange={(e) => handleFieldChange('designation', e.target.value)}
            options={designations}
            placeholder="Select designation"
            disabled={loading}
            renderOption={(d, idx) => (
              <option key={idx} value={typeof d === 'object' ? d.id : d}>
                {typeof d === 'object' ? d.name : d}
              </option>
            )}
          />
        </FormField>

        <FormField label="Department">
          <Select
            id="officer-department"
            value={(() => {
              // If hideDepartmentField is true, find and use the matching department from dropdown
              if (hideDepartmentField && defaultDepartment && departments.length > 0) {
                const matchingDept = departments.find(dept => {
                  const deptId = typeof dept === 'object' ? dept.id : dept;
                  return String(deptId) === String(defaultDepartment);
                });
                if (matchingDept) {
                  return typeof matchingDept === 'object' ? String(matchingDept.id) : String(matchingDept);
                }
              }
              // Otherwise use officer.department, converting to string for consistency
              const deptValue = officer.department || '';
              return typeof deptValue === 'object' ? String(deptValue.id) : String(deptValue);
            })()}
            onChange={(e) => {
              // Only allow change if department field is not hidden
              if (!hideDepartmentField) {
                handleFieldChange('department', e.target.value);
              }
            }}
            options={departments}
            placeholder="Select department"
            disabled={loading || hideDepartmentField}
            renderOption={(d, idx) => {
              const deptId = typeof d === 'object' ? String(d.id) : String(d);
              const deptName = typeof d === 'object' ? d.name : d;
              return (
                <option key={idx} value={deptId}>
                  {deptName}
                </option>
              );
            }}
          />
        </FormField>

        <FormField label="Email" required>
          <Input
            type="email"
            id="officer-email"
            value={officer.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            placeholder="Enter email address"
            disabled={loading}
          />
        </FormField>

        <FormField label="Phone Number" required>
          <Input
            type="tel"
            id="officer-phone"
            value={officer.phone}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            placeholder="Enter 10-digit phone number"
            disabled={loading}
            maxLength={10}
          />
        </FormField>

        {mode === 'edit' && officer.status == 'Delete' && (
          <FormField label="Status">
            <select
              id="officer-status"
              value={officer.status || 'Inactive'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="Active" disabled>Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Delete">Delete</option>
            </select>
          </FormField>
        )}

        <div className="md:col-span-2">
          <FormField label="Authority Privileges" hint="Select privileges for this officer">
            <MultiSelect
              id="officer-menu-ids"
              options={authorityPrivileges}
              value={officer.menuIds || []}
              onChange={(values) => handleFieldChange('menuIds', values)}
              placeholder="Select authority privileges..."
              disabled={loading}
            />
          </FormField>
        </div>
      </div>
    </BaseDialog>
  );
};

export default OfficerDialog;

